import { useEffect, useMemo, useState } from "react";
import Button from "../common/Button";
import BaseModal from "../modals/BaseModal";
import {
  CALL_DISPOSITION_OPTIONS,
  CALL_FOLLOW_UP_STATUS_OPTIONS,
} from "../../utils/constants";

function normalizeStatus(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return CALL_FOLLOW_UP_STATUS_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : "in_progress";
}

function formatDateTimeLocalValue(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function buildInitialState(queueCall) {
  const metadata = queueCall?.metadata || {};

  return {
    status: normalizeStatus(queueCall?.call_status || queueCall?.status),
    disposition: String(queueCall?.disposition || "").trim(),
    note: "",
    callback_time: formatDateTimeLocalValue(metadata.callback_time),
  };
}

export default function UpdateCallStatusModal({
  open,
  busy,
  queueCall,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(buildInitialState(queueCall));

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(buildInitialState(queueCall));
  }, [open, queueCall]);

  const dispositionOptions = useMemo(
    () => CALL_DISPOSITION_OPTIONS,
    []
  );

  return (
    <BaseModal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Update Call Status"
      meta={queueCall ? `${queueCall.patient_name || "Assigned call"} - ${queueCall.phone || "N/A"}` : ""}
      modalId="update-call-status-modal"
      className="team-modal"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();

          onSubmit({
            status: form.status,
            disposition: form.disposition || undefined,
            note: form.note.trim() || undefined,
            callback_time:
              form.status === "callback_required" && form.callback_time
                ? new Date(form.callback_time).toISOString()
                : undefined,
          });
        }}
      >
        <div className="team-modal-grid">
          <label className="outbound-field">
            <span className="outbound-label">Status</span>
            <select
              className="team-modal-select"
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
              disabled={busy}
            >
              {CALL_FOLLOW_UP_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="outbound-field">
            <span className="outbound-label">Disposition</span>
            <select
              className="team-modal-select"
              value={form.disposition}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  disposition: event.target.value,
                }))
              }
              disabled={busy}
            >
              <option value="">Select disposition</option>
              {dispositionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {form.status === "callback_required" ? (
            <label className="outbound-field">
              <span className="outbound-label">Callback Time</span>
              <input
                className="outbound-input"
                type="datetime-local"
                value={form.callback_time}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    callback_time: event.target.value,
                  }))
                }
                disabled={busy}
              />
            </label>
          ) : null}
        </div>

        <label className="outbound-field" style={{ marginTop: 14 }}>
          <span className="outbound-label">Note</span>
          <textarea
            className="campaign-input campaign-textarea"
            rows="4"
            value={form.note}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                note: event.target.value,
              }))
            }
            disabled={busy}
            placeholder="Add a follow-up note..."
          />
        </label>

        <div className="modal-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="gold" disabled={busy}>
            {busy ? "Saving..." : "Save Status"}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
}
